// ============================================
// CHANGE THEME COLORS FROM HERE
// ============================================

export const THEME_COLORS = {
  // Primary Brand Colors (Change these to update entire app theme)
  primary: {
    main: '#4DB6AC',
    light: '#E6F6F4',
    dark: '#2E8B84',
    gradient: 'linear-gradient(135deg, #4DB6AC 0%, #7ED1C7 100%)',
  },

  // Secondary Brand Colors
  secondary: {
    main: '#f093fb',
    light: '#f5576c',
    dark: '#c3cfe2',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },

  // Accent Colors for Dashboard Cards
  accent: {
    blue: '#4285f4',
    purple: '#9c27b0',
    orange: '#ff9800',
    red: '#f44336',
    green: '#22c55e',
    teal: '#20c997',
  },

  // Neutral Colors
  neutral: {
    white: '#ffffff',
    black: '#000000',
    gray: {
      50: '#f8f9fa',
      100: '#e9ecef',
      200: '#dee2e6',
      300: '#ced4da',
      400: '#adb5bd',
      500: '#6c757d',
      600: '#495057',
      700: '#343a40',
      800: '#212529',
      900: '#1a1e21',
    }
  },

  // Status Colors
  status: {
    success: '#22c55e',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',
  },

  // Background Colors (Light Mode)
  // default → drawer / page shell background (near-white)
  // paper   → card / surface background (pure white)
  background: {
    default: '#f8f9fa',
    paper: '#ffffff',
    elevated: '#ffffff',
  }
};

// Dark Mode Colors
export const DARK_THEME_COLORS = {
  primary: {
    main: '#4DB6AC',
    light: '#1a3a38',
    dark: '#2E8B84',
    gradient: 'linear-gradient(135deg, #4DB6AC 0%, #7ED1C7 100%)',
  },

  secondary: {
    main: '#f093fb',
    light: '#f5576c',
    dark: '#c3cfe2',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },

  accent: {
    blue: '#5b9cf6',
    purple: '#b060c0',
    orange: '#ffa726',
    red: '#ef5350',
    green: '#4caf50',
    teal: '#26a69a',
  },

  neutral: {
    white: '#ffffff',
    black: '#000000',
    gray: {
      50: '#1e2124',
      100: '#2a2d31',
      200: '#35393e',
      300: '#454a50',
      400: '#6b7280',
      500: '#9ca3af',
      600: '#d1d5db',
      700: '#e5e7eb',
      800: '#f3f4f6',
      900: '#f9fafb',
    }
  },

  status: {
    success: '#4caf50',
    warning: '#ffa726',
    error: '#ef5350',
    info: '#42a5f5',
  },

  // Background Colors (Dark Mode)
  // default → drawer / page shell background (darkest layer)
  // paper   → card / surface background (sits above default)
  background: {
    default: '#1a1f2e',
    paper: '#242937',
    elevated: '#22262f',
  }
};

// Gradient Combinations
export const GRADIENTS = {
  primary: THEME_COLORS.primary.gradient,
  secondary: THEME_COLORS.secondary.gradient,

  // Dashboard card gradients
  blue: `linear-gradient(135deg, ${THEME_COLORS.accent.blue}15 0%, ${THEME_COLORS.accent.blue}05 100%)`,
  purple: `linear-gradient(135deg, ${THEME_COLORS.accent.purple}15 0%, ${THEME_COLORS.accent.purple}05 100%)`,
  orange: `linear-gradient(135deg, ${THEME_COLORS.accent.orange}15 0%, ${THEME_COLORS.accent.orange}05 100%)`,
  red: `linear-gradient(135deg, ${THEME_COLORS.accent.red}15 0%, ${THEME_COLORS.accent.red}05 100%)`,

  // Special gradients
  glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 100%)',
  overlay: 'linear-gradient(45deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.1))',
};

// Shadow System
export const SHADOWS = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  primary: `0 8px 32px ${THEME_COLORS.primary.main}25`,
  colored: {
    blue: `0 4px 12px ${THEME_COLORS.accent.blue}25`,
    purple: `0 4px 12px ${THEME_COLORS.accent.purple}25`,
    orange: `0 4px 12px ${THEME_COLORS.accent.orange}25`,
    red: `0 4px 12px ${THEME_COLORS.accent.red}25`,
  }
};

export const DARK_SHADOWS = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
  primary: `0 8px 32px ${THEME_COLORS.primary.main}40`,
  colored: {
    blue: `0 4px 12px ${THEME_COLORS.accent.blue}40`,
    purple: `0 4px 12px ${THEME_COLORS.accent.purple}40`,
    orange: `0 4px 12px ${THEME_COLORS.accent.orange}40`,
    red: `0 4px 12px ${THEME_COLORS.accent.red}40`,
  }
};

// Border Radius System
export const BORDER_RADIUS = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  full: '50%',
};

// Spacing System (multipliers of 4px)
export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Function to get color with opacity
export const getColorWithOpacity = (color: string, opacity: number): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Function to create gradient background
export const createGradient = (color1: string, color2: string, direction = '135deg'): string => {
  return `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`;
};

// Function to get themed shadow
export const getThemedShadow = (color: string, intensity = 0.25): string => {
  return `0 4px 12px ${color}${Math.round(intensity * 100).toString(16)}`;
};

export default THEME_COLORS;